import { Box, Typography } from "@mui/material";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import strings from "global/constants/StringConstants";
import { Folder } from "./types";
import FolderTree from "./FolderTree";

export const getTitleText = (type: string) => {
  switch (type) {
    case strings.rename:
      return "Rename Folder";
    case strings.deleteFolder:
      return "Delete Folder";
    case strings.moveFolder:
      return "Move to folder";
    case strings.createNewFolder:
      return "Create new Folder";
    case strings.newSubfolder:
      return "Create new Sub-Folder";
    case strings.deleteAnyway:
      return "Delete Folder Permanently";
    default:
      return "";
  }
};

/**
 * Deleting a folder — and force-deleting one that still holds items — is the
 * only irreversible action here, so those two carry the destructive tone.
 */
const getDialogTone = (dialogType: string) =>
  dialogType === strings.deleteFolder || dialogType === strings.deleteAnyway
    ? ("destructive" as const)
    : ("default" as const);

/** Body of the folder dialog — the field, prompt or tree the type calls for. */
export const FolderDialogBody = ({
  dialogType,
  newFolderName,
  setNewFolderName,
  folderNameError,
  setFolderNameError,
  selectedFolder,
  setSelectedFolder,
  setDestFolder,
  destFolder,
  folderTree,
}: {
  dialogType: string;
  newFolderName: string;
  setNewFolderName: (value: string) => void;
  folderNameError: string;
  setFolderNameError: (value: string) => void;
  selectedFolder?: any | null;
  setSelectedFolder: (value: any) => void;
  setDestFolder: (value: any) => void;
  destFolder: any | null;
  folderTree: Folder[];
}) => {
  switch (dialogType) {
    case strings.rename:
    case strings.createNewFolder:
    case strings.newSubfolder:
      return (
        // The Modal System's body is left-aligned and already inset, so the
        // field no longer needs the centring wrapper the old shell required.
        <Box>
          <CustomInput
            required
            // Create Folder opens straight into the name field so the user can
            // start typing without reaching for the mouse.
            autoFocus={dialogType === strings.createNewFolder}
            id="folderName"
            placeHolder="Enter folder name"
            type="text"
            name="folderName"
            label="Folder name"
            value={newFolderName}
            onChange={(e: any) => {
              setNewFolderName(e.target.value);
              if (folderNameError) setFolderNameError("");
            }}
            error={folderNameError}
            helperText={folderNameError}
          />
        </Box>
      );
    case strings.deleteFolder:
      return (
        <Typography sx={bodyTextSx}>
          Are you sure you want to delete folder?
        </Typography>
      );
    case strings.moveFolder:
      return (
        <>
          <FolderTree
            folderData={folderTree}
            destFolder={destFolder}
            setDestFolder={setDestFolder}
            selectedFolderData={selectedFolder}
            setSelectedFolderData={setSelectedFolder}
            isShowMoveFolderName
          />
        </>
      );
    case strings.deleteAnyway:
      return (
        <>
          <Typography sx={[bodyTextSx, { mb: 2 }]}>
            This folder contains items that cannot be deleted normally.
          </Typography>
          <Typography sx={bodyTextSx} color="error">
            Are you sure you want to force delete this folder and all its
            contents?
          </Typography>
        </>
      );
    default:
      return <></>;
  }
};

export const CustomDialogComponent = ({
  openDialog,
  handleDialogClose,
  dialogType,
  newFolderName,
  setNewFolderName,
  folderNameError,
  setFolderNameError,
  moveFolders,
  createNewFolderFunction,
  renameFolderFunction,
  deleteFolder,
  deleteAnyway,
  selectedFolder,
  setSelectedFolder,
  setDestFolder,
  destFolder,
  folderTree,
  parentId,
}: {
  openDialog: boolean;
  handleDialogClose: () => void;
  dialogType: string;
  newFolderName: string;
  setNewFolderName: (value: string) => void;
  folderNameError: string;
  setFolderNameError: (value: string) => void;
  moveFolders: (folderId: number, destId: number) => void;
  createNewFolderFunction: (value: number) => void;
  renameFolderFunction: () => void;
  deleteFolder: (folderId: number) => void;
  deleteAnyway: (folderId: number) => void;
  selectedFolder?: any | null;
  setSelectedFolder: (value: any) => void;
  setDestFolder: (value: any) => void;
  destFolder: any | null;
  folderTree: Folder[];
  parentId: number;
}) => {
  const handleConfirm = () => {
    switch (dialogType) {
      case strings.rename:
        return renameFolderFunction();
      case strings.newSubfolder:
        return createNewFolderFunction(selectedFolder.autoGeneratedId);
      case strings.createNewFolder:
        return createNewFolderFunction(parentId);
      case strings.moveFolder:
        return moveFolders(selectedFolder.autoGeneratedId, destFolder.folderId);
      case strings.deleteFolder:
        return deleteFolder(selectedFolder.autoGeneratedId);
      case strings.deleteAnyway:
        return deleteAnyway(selectedFolder.autoGeneratedId);
      default:
        return undefined;
    }
  };

  return (
    <AppModal
      open={openDialog}
      onClose={handleDialogClose}
      title={getTitleText(dialogType)}
      tone={getDialogTone(dialogType)}
      // The move dialog carries a folder tree; everything else is a single
      // field or a sentence.
      size={dialogType === strings.moveFolder ? "md" : "sm"}
      primaryLabel={dialogType}
      onPrimary={handleConfirm}
      cancelLabel={strings.cancel}
      onCancel={handleDialogClose}
    >
      <FolderDialogBody
        dialogType={dialogType}
        newFolderName={newFolderName}
        setNewFolderName={setNewFolderName}
        folderNameError={folderNameError}
        setFolderNameError={setFolderNameError}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        setDestFolder={setDestFolder}
        destFolder={destFolder}
        folderTree={folderTree}
      />
    </AppModal>
  );
};
